/**
 * RealtimeSession Component - Mobile-Inspired Design
 * 
 * A professional real-time learning session interface inspired by mobile design:
 * - Clean, focused interface with large video elements
 * - Professional teacher avatar display with DID integration
 * - Real-time WebRTC communication with OpenAI Realtime API
 * - Session controls (start/stop, mute/unmute, transcript)
 * - Attention monitoring and session metrics
 * - Mobile-responsive design with smooth animations
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import ApiService from '../../services/apiService';

const RealtimeSession = () => {
  const { profileId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get teacher and session info from navigation state
  const { teacher, sessionId, isResuming } = location.state || {};
  
  // Session state
  const [sessionActive, setSessionActive] = useState(false);
  const [isInitializingSession, setIsInitializingSession] = useState(false);
  const [learningSessionId, setLearningSessionId] = useState(sessionId || null);
  const [realtimeSessionKey, setRealtimeSessionKey] = useState('');
  const [realtimeSessionStarted, setRealtimeSessionStarted] = useState(false);
  const [realtimeSessionStarting, setRealtimeSessionStarting] = useState(false);
  
  // Teacher and profile state
  const [currentProfileName, setCurrentProfileName] = useState('');
  const [studentPicture, setStudentPicture] = useState(null);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [chatState, setChatState] = useState('idle'); // idle, listening, thinking, speaking
  const [isTranscriptVisible, setTranscriptVisible] = useState(false);
  
  // Media state
  const [studentStream, setStudentStream] = useState(null);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(true);
  const [studentVideoBorder, setStudentVideoBorder] = useState('green');
  
  // Tracking state
  const [learningSessionTimeSeconds, setLearningSessionTimeSeconds] = useState(0);
  const [attentionPercentage, setAttentionPercentage] = useState(100);
  
  // Refs
  const audioRef = useRef(null);
  const inputRef = useRef(null);
  const chatHistoryRef = useRef(null);
  const learningSessionTimerRef = useRef(null);
  const studentVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch initial data
  const fetchInitialData = useCallback(async (userId) => {
    try {
      setLoading(true);
      
      if (!userId || userId === 'undefined' || userId === 'null') {
        console.error('Invalid userId provided to fetchInitialData:', userId);
        setError('Invalid user ID. Please try again.');
        setLoading(false);
        return;
      }
      
      // Fetch profile
      const profileData = await ApiService.getProfile(userId);
      if (profileData) {
        setCurrentProfileName(profileData.name || `${profileData.firstName} ${profileData.lastName}`);
        setStudentPicture(profileData.avatar);
      }
      
      // If resuming, load existing session data
      if (isResuming && learningSessionId) {
        const sessionData = await ApiService.getLearningSession(learningSessionId);
        setLearningSessionTimeSeconds(sessionData.time_minutes * 60 || 0);
        setAttentionPercentage(sessionData.attention_percentage || 100);
        
        // Load chat history
        const historyData = await ApiService.getChatHistory(userId, sessionData.chatId, 50);
        setMessages(historyData.messages || []);
      }
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isResuming, learningSessionId]);

  // Fetch initial data on mount
  useEffect(() => {
    if (!profileId && !currentUser) return;
    
    const userId = profileId || currentUser?.uid;
    if (!userId) {
      console.error('No valid user ID available');
      setLoading(false);
      return;
    }
    
    fetchInitialData(userId);
  }, [profileId, currentUser, fetchInitialData]);

  // Start learning session timer
  useEffect(() => {
    if (sessionActive) {
      learningSessionTimerRef.current = setInterval(() => {
        setLearningSessionTimeSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (learningSessionTimerRef.current) {
        clearInterval(learningSessionTimerRef.current);
      }
    }
    
    return () => {
      if (learningSessionTimerRef.current) {
        clearInterval(learningSessionTimerRef.current);
      }
    };
  }, [sessionActive]);

  // Start student camera
  const startStudentCamera = useCallback(async () => {
    try {
      console.log('Requesting camera and microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      console.log('Camera and microphone access granted:', stream);
      console.log('Video tracks:', stream.getVideoTracks());
      console.log('Audio tracks:', stream.getAudioTracks());
      setStudentStream(stream);
      return stream;
    } catch (err) {
      console.error('Error starting camera:', err);
      setError('Failed to access camera/microphone. Please grant permissions.');
      throw err;
    }
  }, []);

  // Stop student camera
  const stopStudentCamera = useCallback(() => {
    if (studentStream) {
      studentStream.getTracks().forEach(track => track.stop());
      setStudentStream(null);
    }
  }, [studentStream]);

  // WebRTC: Handle realtime session messages via data channel
  const handleRealtimeSessionMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      console.log('Received realtime message:', data);
      
      if (data.type === 'response.audio_transcript.delta') {
        setChatState('speaking');
        const transcript = data.delta || '';
        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.startsWith('Teacher:')) {
            const updated = [...prev];
            updated[updated.length - 1] = lastMsg + transcript;
            return updated;
          }
          return [...prev, `Teacher: ${transcript}`];
        });
      } else if (data.type === 'response.audio_transcript.done') {
        setTimeout(() => setChatState('idle'), 1000);
      } else if (data.type === 'input_audio_buffer.speech_started') {
        setChatState('listening');
      } else if (data.type === 'input_audio_buffer.speech_stopped') {
        setChatState('thinking');
      } else if (data.type === 'conversation.item.input_audio_transcription.completed') {
        const transcript = data.transcript || '';
        setMessages(prev => [...prev, `Student: ${transcript}`]);
      }
    } catch (err) {
      console.error('Error handling realtime message:', err);
    }
  }, []);

  // Send session configuration to OpenAI
  const sendSessionConfig = useCallback(() => {
    if (!dataChannelRef.current) return;

    const config = {
      type: 'session.update',
      session: {
        modalities: ['text', 'audio'],
        instructions: teacher?.personality || 'You are a helpful teacher.',
        voice: teacher?.voice || 'alloy',
        input_audio_format: 'pcm16',
        output_audio_format: 'pcm16',
        input_audio_transcription: {
          model: 'whisper-1'
        },
        turn_detection: {
          type: 'server_vad',
          threshold: 0.5,
          prefix_padding_ms: 300,
          silence_duration_ms: 500
        }
      }
    };

    try {
      dataChannelRef.current.send(JSON.stringify(config));
      console.log('Sent session config');
    } catch (err) {
      console.error('Error sending session config:', err);
    }
  }, [teacher]);

  // WebRTC: Start realtime session with OpenAI
  const startRealtimeSessionWebRTC = useCallback(async () => {
    if (!realtimeSessionKey || !studentStream || !teacher) {
      console.error('Missing required data for WebRTC session');
      return;
    }

    // Prevent multiple connections
    if (peerConnectionRef.current && peerConnectionRef.current.connectionState !== 'closed') {
      console.log('WebRTC connection already exists, skipping...');
      return;
    }

    try {
      console.log('Starting realtime session with teacher:', {
        teacherId: teacher?.id,
        teacherVoice: teacher?.voice,
        teacherName: teacher?.name
      });
      
      setChatState('thinking');
      setRealtimeSessionStarting(true);

      // Clean up any existing connection first
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      if (dataChannelRef.current) {
        dataChannelRef.current.close();
      }

      // Create RTCPeerConnection
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' }
        ],
        rtcpMuxPolicy: 'require'
      });

      peerConnectionRef.current = pc;

      // Create data channel for messaging
      const dc = pc.createDataChannel('chat');
      dc.onopen = () => {
        console.log('Data channel opened');
        dataChannelRef.current = dc;
      };
      dc.onclose = () => {
        console.log('Data channel closed');
        dataChannelRef.current = null;
      };
      dc.onmessage = handleRealtimeSessionMessage;

      // Monitor connection state
      pc.onconnectionstatechange = () => {
        console.log('Connection state changed:', pc.connectionState);
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          console.error('WebRTC connection failed or disconnected');
          setError('Connection lost. Please try restarting the session.');
        }
      };

      // Handle incoming audio from OpenAI
      const audioElement = audioRef.current;
      if (audioElement) {
        pc.ontrack = (event) => {
          console.log('Received audio track from OpenAI');
          audioElement.srcObject = event.streams[0];
          audioElement.play().catch(e => console.error('Error playing audio:', e));
        };
      }

      // Add student microphone track to peer connection
      if (studentStream) {
        const audioTrack = studentStream.getAudioTracks()[0];
        if (audioTrack) {
          console.log('Adding student audio track to peer connection');
          pc.addTrack(audioTrack, studentStream);
        }
      }

      // Create offer
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: false
      });

      // Set local description
      await pc.setLocalDescription(offer);

      // Send SDP to OpenAI Realtime API
      const baseUrl = 'https://api.openai.com/v1/realtime';
      const model = 'gpt-4o-mini-realtime-preview-2024-12-17';
      
      const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          'Authorization': `Bearer ${realtimeSessionKey}`,
          'Content-Type': 'application/sdp'
        }
      });

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text();
        console.error('SDP Response Error:', errorText);
        throw new Error(`SDP response error: ${sdpResponse.status} ${errorText}`);
      }

      const answerSdp = await sdpResponse.text();
      console.log('Received answer SDP from OpenAI');

      // Set remote description
      await pc.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp
      });

      setRealtimeSessionStarted(true);
      setChatState('idle');
      
      // Send initial session configuration
      sendSessionConfig();
    } catch (err) {
      console.error('Error starting WebRTC session:', err);
      setChatState('idle');
      setError(`WebRTC Error: ${err.message}`);
    } finally {
      setRealtimeSessionStarting(false);
    }
  }, [realtimeSessionKey, studentStream, teacher, handleRealtimeSessionMessage, sendSessionConfig]);

  // Start realtime session when key is available - only once
  useEffect(() => {
    if (realtimeSessionKey && studentStream && !realtimeSessionStarted && !realtimeSessionStarting) {
      console.log('Starting WebRTC realtime session');
      startRealtimeSessionWebRTC();
    }
  }, [realtimeSessionKey, studentStream, realtimeSessionStarted, realtimeSessionStarting, startRealtimeSessionWebRTC]);

  // Cleanup WebRTC connections only on unmount
  useEffect(() => {
    return () => {
      if (peerConnectionRef.current) {
        console.log('Cleaning up peer connection on unmount');
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      if (dataChannelRef.current) {
        console.log('Cleaning up data channel on unmount');
        dataChannelRef.current.close();
        dataChannelRef.current = null;
      }
    };
  }, []);

  // Start new session
  const startNewSession = useCallback(async () => {
    if (!teacher) {
      setError('No teacher selected');
      return;
    }

    setIsInitializingSession(true);
    setError(null);

    try {
      const userId = profileId || currentUser?.uid;
      if (!userId) {
        setError('User not authenticated');
        setIsInitializingSession(false);
        return;
      }
      
      // Start camera
      await startStudentCamera();
      
      // Create learning session first
      console.log('Creating learning session with data:', {
        userId,
        teacherId: teacher.id,
        generate_lesson_plan: true
      });
      
      const sessionData = await ApiService.createOrUpdateLearningSession({
        userId,
        teacherId: teacher.id,
        generate_lesson_plan: true
      });
      
      console.log('Learning session created successfully:', sessionData);
      
      if (!sessionData.learningSessionId) {
        throw new Error('Learning session was created but no session ID returned');
      }
      
      setLearningSessionId(sessionData.learningSessionId);
      setLearningSessionTimeSeconds(0);
      
      // Get realtime session key with learning session ID
      console.log('Getting realtime session key with data:', {
        userId,
        teacherId: teacher.id,
        learningSessionId: sessionData.learningSessionId
      });
      
      const realtimeData = await ApiService.getSessionKey({
        userId,
        teacherId: teacher.id,
        learningSessionId: sessionData.learningSessionId
      });
      
      console.log('Realtime session key received:', realtimeData);
      
      if (!realtimeData.key) {
        throw new Error('Realtime session key was not returned from server');
      }
      
      setRealtimeSessionKey(realtimeData.key);
      setSessionActive(true);
      
      // Add welcome message
      setMessages(['Teacher: Hello! I\'m ready to help you learn. What would you like to work on today?']);
    } catch (err) {
      console.error('Error starting session:', err);
      setError(err.message);
      stopStudentCamera();
    } finally {
      setIsInitializingSession(false);
    }
  }, [teacher, profileId, currentUser, startStudentCamera, stopStudentCamera]);

  // Continue existing session
  const continueSession = useCallback(async () => {
    if (!teacher || !learningSessionId) {
      setError('No session to continue');
      return;
    }

    setIsInitializingSession(true);
    setError(null);

    try {
      const userId = profileId || currentUser?.uid;
      if (!userId) {
        setError('User not authenticated');
        setIsInitializingSession(false);
        return;
      }
      
      // Start camera
      await startStudentCamera();
      
      // Get realtime session key with existing learning session ID
      console.log('Getting realtime session key for existing session with data:', {
        userId,
        teacherId: teacher.id,
        learningSessionId: learningSessionId
      });
      
      const realtimeData = await ApiService.getSessionKey({
        userId,
        teacherId: teacher.id,
        learningSessionId: learningSessionId
      });
      
      console.log('Realtime session key received:', realtimeData);
      
      if (!realtimeData.key) {
        throw new Error('Realtime session key was not returned from server');
      }
      
      setRealtimeSessionKey(realtimeData.key);
      setSessionActive(true);
    } catch (err) {
      console.error('Error continuing session:', err);
      setError(err.message);
      stopStudentCamera();
    } finally {
      setIsInitializingSession(false);
    }
  }, [teacher, learningSessionId, profileId, currentUser, startStudentCamera, stopStudentCamera]);

  // Stop session
  const stopSession = useCallback(async () => {
    try {
      console.log('Stopping session and cleaning up WebRTC connections...');
      
      // Close WebRTC connections properly
      if (dataChannelRef.current) {
        console.log('Closing data channel...');
        dataChannelRef.current.close();
        dataChannelRef.current = null;
      }
      
      if (peerConnectionRef.current) {
        console.log('Closing peer connection...');
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }

      if (learningSessionId) {
        // Update session time
        console.log('Updating learning session...');
        const token = await currentUser.getIdToken();
        await ApiService.updateLearningSession(learningSessionId, {
          time_minutes: Math.floor(learningSessionTimeSeconds / 60),
          attention_percentage: attentionPercentage
        }, token);
      }
      
      stopStudentCamera();
      setSessionActive(false);
      setRealtimeSessionStarted(false);
      setRealtimeSessionKey('');
      setLearningSessionTimeSeconds(0);
      setChatState('idle');
      
      console.log('Session stopped successfully');
    } catch (err) {
      console.error('Error stopping session:', err);
      setError(err.message);
    }
  }, [learningSessionId, learningSessionTimeSeconds, attentionPercentage, stopStudentCamera]);

  // Toggle transcript visibility
  const toggleTranscriptVisibility = useCallback(() => {
    setTranscriptVisible(prev => !prev);
  }, []);

  // Toggle microphone
  const toggleMicrophone = useCallback(() => {
    if (studentStream) {
      const audioTrack = studentStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !microphoneEnabled;
        setMicrophoneEnabled(!microphoneEnabled);
      }
    }
  }, [studentStream, microphoneEnabled]);

  // Connect student video stream
  useEffect(() => {
    if (studentVideoRef.current && studentStream) {
      console.log('Connecting student video stream to video element');
      studentVideoRef.current.srcObject = studentStream;
      
      // Add event listeners for debugging
      studentVideoRef.current.onloadedmetadata = () => {
        console.log('Student video metadata loaded');
      };
      
      studentVideoRef.current.oncanplay = () => {
        console.log('Student video can play');
      };
      
      studentVideoRef.current.onerror = (e) => {
        console.error('Student video error:', e);
      };
    }
  }, [studentStream]);

  // Auto-scroll chat history
  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, [messages]);

  // Control microphone based on chat state
  useEffect(() => {
    if (!studentStream) return;

    const audioTrack = studentStream.getAudioTracks()[0];
    if (!audioTrack) return;

    if (chatState === 'thinking' || chatState === 'speaking') {
      audioTrack.enabled = false;
    } else if (chatState === 'idle' && microphoneEnabled) {
      audioTrack.enabled = true;
    }
  }, [chatState, studentStream, microphoneEnabled]);

  // Format time display
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-4 animate-pulse">
              <svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Loading Your Learning Environment</h3>
            <p className="text-gray-600">Setting up your personalized session...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Teacher Selected</h3>
            <p className="text-gray-600 mb-6">Please select a teacher to start your learning session.</p>
            <button
              onClick={() => navigate(`/chat/${profileId || currentUser?.uid}/properties`)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300"
            >
              Select Teacher
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 pt-28 pb-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-red-800 mb-1">Error</h4>
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <button 
                onClick={() => setError(null)}
                className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Mobile-Inspired Session Interface */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Session Controls Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigate(`/chat/${profileId || currentUser?.uid}/properties`)}
                className="flex items-center gap-2 text-white hover:text-blue-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="font-medium">Back</span>
              </button>
              
              <div className="text-center">
                <h1 className="text-xl font-bold text-white">{teacher.name}</h1>
                <p className="text-blue-100 text-sm">{teacher.specialty}</p>
              </div>
              
              <div className="w-20"></div> {/* Spacer for centering */}
            </div>

            {/* Main Session Control Button */}
            {!sessionActive ? (
              <button
                onClick={isResuming ? continueSession : startNewSession}
                disabled={isInitializingSession}
                className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 ${
                  isInitializingSession 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-white text-blue-600 hover:bg-blue-50 hover:shadow-lg transform hover:scale-105'
                }`}
              >
                {isInitializingSession ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isResuming ? 'Resuming Session...' : 'Starting Session...'}
                  </span>
                ) : (
                  isResuming ? 'Continue Session' : 'Start Session'
                )}
              </button>
            ) : (
              <button
                onClick={stopSession}
                className="w-full py-4 px-6 bg-red-600 text-white rounded-xl font-bold text-lg hover:bg-red-700 transition-all duration-300 hover:shadow-lg transform hover:scale-105"
              >
                Stop Session
              </button>
            )}
          </div>

          {/* Session Status and Controls */}
          {sessionActive && (
            <div className="p-6 bg-gray-50 border-b">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={toggleTranscriptVisibility}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-medium"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                    {isTranscriptVisible ? 'Hide Transcript' : 'Show Transcript'}
                  </button>

                  <button
                    onClick={toggleMicrophone}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      microphoneEnabled 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      {microphoneEnabled ? (
                        <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                      ) : (
                        <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                      )}
                    </svg>
                    {microphoneEnabled ? 'Unmuted' : 'Muted'}
                  </button>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-800">
                      {formatTime(learningSessionTimeSeconds)}
                    </div>
                    <div className="text-sm text-gray-600">Session Time</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {attentionPercentage}%
                    </div>
                    <div className="text-sm text-gray-600">Attention</div>
                  </div>
                </div>
              </div>

              {/* Connection Status */}
              {realtimeSessionStarted && (
                <div className="flex items-center justify-center gap-2 text-sm text-green-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="font-medium">Real-time Connection Active</span>
                </div>
              )}
            </div>
          )}

          {/* Main Video Section */}
          {!isTranscriptVisible ? (
            <div className="p-8">
              <div className="flex flex-col items-center">
                {/* Teacher Avatar - Large, Professional Display */}
                <div className="relative mb-8">
                  <div className="w-80 h-80 rounded-full overflow-hidden shadow-2xl border-4 border-white">
                    {teacher.picture ? (
                      <img
                        src={teacher.picture}
                        alt={teacher.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <span className="text-8xl font-bold text-white">
                          {teacher.name.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Speaking Indicator */}
                  {chatState === 'speaking' && (
                    <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
                      <div className="flex gap-1">
                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"></div>
                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Student Video - Smaller, Bottom Right */}
                <div className="relative">
                  <div className={`w-32 h-32 rounded-full overflow-hidden shadow-lg border-4 ${
                    studentVideoBorder === 'green' ? 'border-green-500' :
                    studentVideoBorder === 'red' ? 'border-red-500' :
                    'border-yellow-500'
                  }`}>
                    {studentStream ? (
                      <video
                        ref={studentVideoRef}
                        autoPlay
                        muted
                        playsInline
                        className="w-full h-full object-cover"
                        style={{ transform: 'scaleX(-1)' }}
                      />
                    ) : studentPicture ? (
                      <img
                        src={studentPicture}
                        alt="Student"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">
                          {currentProfileName?.charAt(0) || 'S'}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Listening Indicator */}
                  {chatState === 'listening' && (
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Session Status */}
                <div className="mt-6 text-center">
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                    chatState === 'idle' ? 'bg-gray-100 text-gray-700' :
                    chatState === 'listening' ? 'bg-yellow-100 text-yellow-700' :
                    chatState === 'thinking' ? 'bg-blue-100 text-blue-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${
                      chatState === 'idle' ? 'bg-gray-500' :
                      chatState === 'listening' ? 'bg-yellow-500 animate-pulse' :
                      chatState === 'thinking' ? 'bg-blue-500 animate-pulse' :
                      'bg-green-500 animate-pulse'
                    }`}></div>
                    <span className="capitalize">{chatState}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Transcript View */
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Session Transcript</h3>
                <p className="text-gray-600 text-sm">
                  Real-time conversation with {teacher.name}
                </p>
              </div>

              <div 
                ref={chatHistoryRef}
                className="h-96 overflow-y-auto border-2 border-gray-200 rounded-lg p-4 bg-gray-50"
              >
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p>No messages yet. Start speaking to begin the conversation.</p>
                  </div>
                ) : (
                  messages.map((msg, index) => {
                    const isStudentMessage = msg.startsWith('Student:');
                    const cleanMessage = msg.replace(/^(Student:|Teacher:)\s*/, '');
                    return (
                      <div key={index} className={`flex ${isStudentMessage ? 'justify-end' : 'justify-start'} mb-4`}>
                        <div className={`flex gap-3 max-w-[80%] ${isStudentMessage ? 'flex-row-reverse' : 'flex-row'}`}>
                          <div className="flex-shrink-0">
                            {isStudentMessage ? (
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-md">
                                <span className="text-white font-bold text-sm">
                                  {currentProfileName?.charAt(0) || 'S'}
                                </span>
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-blue-600 flex items-center justify-center shadow-md">
                                <span className="text-white font-bold text-sm">
                                  {teacher?.name?.charAt(0) || 'T'}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div>
                            <div className={`text-xs font-medium mb-1 ${isStudentMessage ? 'text-right' : 'text-left'}`}>
                              <span className={isStudentMessage ? 'text-blue-600' : 'text-green-600'}>
                                {isStudentMessage ? (currentProfileName || 'You') : (teacher?.name || 'Teacher')}
                              </span>
                            </div>
                            <div 
                              className={`p-3 rounded-2xl shadow-sm ${
                                isStudentMessage 
                                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-tr-none' 
                                  : 'bg-white text-gray-900 border border-gray-200 rounded-tl-none'
                              }`}
                            >
                              <p className="text-sm leading-relaxed">{cleanMessage}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Note Section */}
          <div className="p-6 bg-blue-50 border-t">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">Learning Session Note</h4>
                <p className="text-blue-800 text-sm">
                  This meeting monitors your attention to the course. Please stay focused and maintain eye contact with the camera for the best learning experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden audio element for teacher voice */}
      <audio ref={audioRef} autoPlay />

      <Footer />
    </div>
  );
};

export default RealtimeSession;